import { Box, useBreakpointValue } from '@chakra-ui/react';
import { Button, Container, Group, NakedButton, Text, useDisclosure, useFormSubmissionController } from '@melio/penny';
import { AccountingPlatform, useVendor } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import React, { useState } from 'react';

import { useVendorSelectSideEffects } from '../../../../utils/useVendorSelectSideEffects';
import { InvoiceNumberSchema, SetInvoiceNumberModal } from '../../../set-invoice-number-modal';
import { AddInstantBillV2FormActivity } from '../../AddInstantBillV2Form/AddInstantBillV2Form.activity';
import { AddInstantBillV2FormActivityFields } from '../../AddInstantBillV2Form/types';
import { AddInstantBillV2SubmitEnum, AddInstantBillV2SubmitType } from '../types';

type AddInstantBillV2ScreenProps = {
  defaultValues?: Partial<AddInstantBillV2FormActivityFields>;
  isSaving?: boolean;
  activeAccountingPlatform?: AccountingPlatform;
  isRecurringPaymentsEnabled?: boolean;
  isInternationalSupportedForRecurringFlow?: boolean;
  isRecurringFrequency?: boolean;
  onUpdateFrequency: (isRecurring: boolean) => void;
  onDone: (
    data: AddInstantBillV2FormActivityFields,
    event?: React.MouseEvent<HTMLButtonElement>,
    target?: AddInstantBillV2SubmitType
  ) => void;
  onAdvanceBilldetails: (formValues?: AddInstantBillV2FormActivityFields) => void;
  onClose: VoidFunction;
};

export const AddInstantBillV2Screen: React.FC<AddInstantBillV2ScreenProps> = ({
  defaultValues,
  isSaving,
  activeAccountingPlatform,
  isRecurringPaymentsEnabled,
  isInternationalSupportedForRecurringFlow,
  isRecurringFrequency,
  onUpdateFrequency,
  onDone,
  onAdvanceBilldetails,
  ...props
}) => {
  const { formatMessage } = useMelioIntl();
  const [savedSubmitType, setSavedSubmitType] = useState<AddInstantBillV2SubmitEnum>();
  const { onSubmissionStateChange, submitButtonProps, handleSubmit, getValues, setValue, trigger } =
    useFormSubmissionController<AddInstantBillV2FormActivityFields>();
  const {
    isOpen: isShowAddInvoiceNumberModal,
    onOpen: onShowAddInvoiceNumberModal,
    onClose: onCloseAddInvoiceNumberModal,
  } = useDisclosure();
  const [vendorId, setVendorId] = useState<string | undefined>(defaultValues?.vendorId);
  const { isSuvcOverStpVendor, isFetching: isFetchingVendorSideEffect } = useVendorSelectSideEffects(vendorId);
  const { data: selectedVendor } = useVendor({ id: vendorId, enabled: false });

  const variant: 'vertical' | 'horizontal' | undefined = useBreakpointValue({
    xs: 'vertical',
    s: 'vertical',
    m: 'horizontal',
  });

  const handleFormSubmit = (submitType?: AddInstantBillV2SubmitEnum) => async (): Promise<void> => {
    const isFormValid = await trigger?.();

    if (isSuvcOverStpVendor && isFormValid) {
      setSavedSubmitType(submitType);
      onShowAddInvoiceNumberModal();
    } else {
      handleSubmit?.(submitType)();
    }
  };

  const handleUpdateInvoiceNumber = ({ invoiceNumber }: InvoiceNumberSchema) => {
    setValue?.('invoiceNumber', invoiceNumber);
    onCloseAddInvoiceNumberModal();
    handleSubmit?.(savedSubmitType)();
  };

  const handleSkipAddingInvoiceNumber = () => {
    onCloseAddInvoiceNumberModal();
    handleSubmit?.(savedSubmitType)();
  };

  return (
    <Container
      data-testid="add-instant-bill-v2-activity-add-instant-bill-screen-v2"
      data-component="AddInstantBillV2Activity.AddInstantBillV2Screen"
      {...props}
    >
      <Container paddingBottom="l">
        <Text as="h1" textStyle="heading1Semi" aria-live="polite">
          {formatMessage('activities.addInstantBillV2.screens.addInstantBillV2.title')}
        </Text>
      </Container>
      <Box w="100%">
        <AddInstantBillV2FormActivity
          defaultValues={defaultValues}
          activeAccountingPlatform={activeAccountingPlatform}
          isRecurringPaymentsEnabled={isRecurringPaymentsEnabled}
          isInternationalSupportedForRecurringFlow={isInternationalSupportedForRecurringFlow}
          isSaving={isSaving}
          onSubmit={onDone}
          onSubmissionStateChange={onSubmissionStateChange}
          onUpdateFrequency={onUpdateFrequency}
          onSelectedVendorChange={setVendorId}
          isLoading={isFetchingVendorSideEffect}
        />
      </Box>

      <Box pt="l">
        <NakedButton
          variant="secondary"
          isDisabled={isRecurringFrequency || isSaving}
          onClick={() => onAdvanceBilldetails(getValues?.())}
          data-testid="add-bill-details-screen-v2-advance"
          label={formatMessage('activities.addInstantBillV2.screens.addInstantBillV2.advanceBillDetails')}
        />
      </Box>

      <Box pt="l" pb="l">
        <Group justifyContent="flex-end" variant={variant}>
          {!isRecurringFrequency && (
            <Button
              label={formatMessage('activities.addInstantBillV2.screens.addInstantBillV2.close')}
              variant="secondary"
              data-testid="add-bill-details-screen-v2-secondary-button"
              {...submitButtonProps}
              onClick={handleFormSubmit(AddInstantBillV2SubmitEnum.SAVE_AND_CLOSE)}
              isDisabled={submitButtonProps?.isDisabled || isFetchingVendorSideEffect}
            />
          )}
          <Button
            label={formatMessage('activities.addInstantBillV2.screens.addInstantBillV2.continue')}
            data-testid="add-bill-details-screen-v2-primary-button"
            {...submitButtonProps}
            onClick={handleFormSubmit(AddInstantBillV2SubmitEnum.CONTINUE_TO_PAY)}
            isDisabled={submitButtonProps?.isDisabled || isFetchingVendorSideEffect}
          />
        </Group>
      </Box>
      {selectedVendor ? (
        <SetInvoiceNumberModal
          analytics={{
            contextView: 'Bill',
          }}
          onContinueWithoutInvoiceNumber={handleSkipAddingInvoiceNumber}
          onClose={handleSkipAddingInvoiceNumber}
          isOpen={isShowAddInvoiceNumberModal}
          onSubmitInvoiceNumber={handleUpdateInvoiceNumber}
          vendorName={selectedVendor.name}
        />
      ) : null}
    </Container>
  );
};
